import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import axios, { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getEventIdFromSlug } from 'utils/events';
import {
  API_REQUEST_STATUS_FAIL,
  API_REQUEST_STATUS_NOT_SENT,
  API_REQUEST_STATUS_PENDING,
  API_REQUEST_STATUS_SUCCESS,
} from 'utils/static/constants';
import { FreeEventRegistrationPageData } from './useFreeEventData';

interface ApplyPrivateInvitationProps {
  numberOfPublicTicketTypes: number;
  setData: Dispatch<SetStateAction<FreeEventRegistrationPageData>>;
}

interface FormData {
  code: string;
}

const ApplyPrivateInvitation = ({
  numberOfPublicTicketTypes,
  setData,
}: ApplyPrivateInvitationProps) => {
  const { t } = useTranslation();
  const { eventSlug } = useParams();
  const eventId = getEventIdFromSlug(eventSlug);
  const [requestStatus, setRequestStatus] = useState(
    API_REQUEST_STATUS_NOT_SENT,
  );

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setRequestStatus(API_REQUEST_STATUS_PENDING);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/event/${eventId}/get_ticket_type_by_private_code/${data.code}?include=isWhitelisted`,
      );
      setRequestStatus(API_REQUEST_STATUS_SUCCESS);
      toast.success(t('freeEventRegistrationPage.privateInvitationSuccess'), {
        position: 'bottom-right',
        autoClose: 5000,
      });

      setData(prevData => ({
        ...prevData,
        ticketTypes: [
          ...prevData.ticketTypes.filter(
            ticketType => ticketType.id !== response.data.id,
          ),
          {
            id: response.data.id,
            name: response.data.name,
            isWhitelisted: response.data.isWhitelisted,
          },
        ],
      }));
    } catch (error) {
      setRequestStatus(API_REQUEST_STATUS_FAIL);
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data.error.message;
        let message;
        switch (true) {
          case errorMessage === undefined:
            message = t('inputErrors.anErrorOccured');
            break;
          case /invalid/i.test(errorMessage):
            message = t('freeEventRegistrationPage.invalidInvitationCode');
            break;
          case /ended/i.test(errorMessage):
            message = t(
              'freeEventRegistrationPage.invitationCodeIsNoLongerValid',
            );
            break;
        }

        setError('code', {
          message,
        });
      } else {
        setError('code', {
          message: t('inputErrors.anErrorOccured') as string,
        });
      }
    }
  };

  return (
    <>
      {numberOfPublicTicketTypes === 0 ? (
        <div className="mb-4">
          <p className="title-sm">{t('ticketSale.privateEventTitle')}</p>
          <p className="p2-regular mt-1">
            {t('ticketSale.privateEventSubtitle')}
          </p>
        </div>
      ) : (
        <label className="label-input p3-semibold">
          {t('freeEventRegistrationPage.privateInvitationLabel')}
        </label>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="relative">
          <Input
            placeholder={t('ticketSale.code') as string}
            className="pr-18"
            {...register('code', {
              required: {
                value: true,
                message: t('inputErrors.requiredField') as string,
              },
            })}
          />
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-1 right-1"
            disabled={requestStatus === API_REQUEST_STATUS_PENDING}
          >
            {requestStatus === API_REQUEST_STATUS_PENDING ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('ticketSale.apply')
            )}
          </Button>
        </div>
        {errors.code ? (
          <p className="standard-input-error mt-1">{errors.code.message}</p>
        ) : null}
      </form>
    </>
  );
};

export default ApplyPrivateInvitation;
